/**
 * spk-migrate.ts — one-time client migration off the legacy hardcoded
 * signedPreKey.keyId = 1 onto a device-unique id (lib/spk-id.ts).
 *
 * Rotating retains the legacy SPK in previousSignedPreKeys so any X3DH wrap a
 * peer already built against keyId=1 (before this device republished) still
 * decrypts during the cutover window. The caller (useCrypto init) must re-register
 * the returned bundle so the server publishes the new unique SPK.
 *
 * No SQL migration: signed_pre_key.keyId lives in an existing jsonb column; the
 * server stores whatever number the client publishes.
 */
import { type DeviceKeys, rotateSignedPreKey } from "@kollegeplus/crypto";
import { nextSpkId, SPK_ID_MIN_UNIQUE } from "./spk-id";

const MIGRATED_FLAG = "kp_spk_migrated";

export interface SpkMigrationResult {
  deviceKeys: DeviceKeys;
  /** True when a rotation happened and the caller must re-register + persist. */
  migrated: boolean;
}

/** Rotate a legacy (keyId < SPK_ID_MIN_UNIQUE) device onto a unique id exactly
 *  once. Idempotent: returns the input untouched when already unique or already
 *  migrated. Pure w.r.t. the keys (returns a new DeviceKeys); sets the localStorage
 *  flag as a side effect. */
export function migrateLegacySpk(deviceKeys: DeviceKeys): SpkMigrationResult {
  if (localStorage.getItem(MIGRATED_FLAG) === "1") {
    return { deviceKeys, migrated: false };
  }
  if (deviceKeys.signedPreKey.keyId >= SPK_ID_MIN_UNIQUE) {
    localStorage.setItem(MIGRATED_FLAG, "1");
    return { deviceKeys, migrated: false };
  }
  const rotated = rotateSignedPreKey(deviceKeys, nextSpkId());
  localStorage.setItem(MIGRATED_FLAG, "1");
  return { deviceKeys: rotated, migrated: true };
}

/** Init-time wrapper: migrate legacy SPK and, when a rotation happened, clear the
 *  cached keys fingerprint so the init path's `needsRegistration` flips true and
 *  republishes the new unique SPK to the server. Returns the keys to install. */
export function applySpkMigrationOnInit(deviceKeys: DeviceKeys): DeviceKeys {
  const { deviceKeys: migrated, migrated: didRotate } = migrateLegacySpk(deviceKeys);
  if (didRotate) {
    localStorage.removeItem("kp_keys_fingerprint");
  }
  return migrated;
}
